<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import AuthAsciiArt from "../components/AuthAsciiArt.vue"
import { isDashboardApiError } from "../composables/useDashboardApi"
import { useAuthStore } from "../stores/auth"

const auth = useAuthStore()
const route = useRoute()
const router = useRouter()
const dashboardPassword = ref("")
const error = ref("")
const lightsOut = ref(false)
const passwordInput = ref<HTMLInputElement | null>(null)
const progressPercent = ref(0)
const submitting = ref(false)
const cliHistoryLines = [
  "UNAUTHENTICATED"
] as const
const progressFillGlyphs = "⣿".repeat(96)
const progressRailGlyphs = "-".repeat(160)
const progressTrackGlyphs = "⣀".repeat(96)
const passwordEcho = computed(() => "*".repeat(Math.min(dashboardPassword.value.length, 72)))
const progressLabel = computed(() => `${String(progressPercent.value).padStart(3, "0")}%`)
const progressStatus = computed(() => (progressPercent.value >= 100 ? "READY" : "VERIFYING"))
const progressStyle = computed(() => ({ "--auth-progress": `${progressPercent.value}%` }))
let progressAnimationFrame = 0

onMounted(() => {
  const startedAt = performance.now()
  const durationMs = 1120

  const animateProgress = (timestamp: number) => {
    const elapsed = Math.min((timestamp - startedAt) / durationMs, 1)
    const eased = 1 - (1 - elapsed) ** 3
    progressPercent.value = Math.round(eased * 100)

    if (elapsed < 1) {
      progressAnimationFrame = window.requestAnimationFrame(animateProgress)
    }
  }

  progressAnimationFrame = window.requestAnimationFrame(animateProgress)
})

onBeforeUnmount(() => {
  if (progressAnimationFrame) {
    window.cancelAnimationFrame(progressAnimationFrame)
  }
})

function engageLightsOut() {
  lightsOut.value = true
}

function restoreWhiteMode() {
  lightsOut.value = false
}

function focusPasswordInput() {
  engageLightsOut()
  passwordInput.value?.focus()
}

async function submit() {
  if (submitting.value) {
    return
  }

  error.value = ""
  submitting.value = true

  try {
    if (!(await auth.login(dashboardPassword.value))) {
      error.value = "ENTER THE PASSWORD."
      return
    }

    const redirect = typeof route.query.redirect === "string" ? route.query.redirect : "/"
    await router.push(redirect)
  } catch (reason) {
    error.value = loginFailureMessage(reason)
  } finally {
    submitting.value = false
  }
}

function loginFailureMessage(reason: unknown): string {
  if (isDashboardApiError(reason) && reason.status === 401) {
    return "ACCESS DENIED."
  }

  if (isDashboardApiError(reason)) {
    return "UNABLE TO VERIFY ACCESS."
  }

  return "ACCESS CHECK FAILED."
}
</script>

<template>
  <main
    :class="['dashboard-auth-shell', { 'dashboard-auth-shell--dark': lightsOut }]"
    data-testid="auth-shell"
  >
    <section class="dashboard-auth-terminal" aria-labelledby="auth-title">
      <h1 id="auth-title" class="sr-only">NETWORK OPERATIONS</h1>

      <div class="dashboard-auth-cli-history" aria-hidden="true">
        <AuthAsciiArt />
        <pre class="dashboard-auth-cli-line dashboard-auth-cli-blank"> </pre>
        <div class="dashboard-auth-cli-check" :style="progressStyle">
          <div class="dashboard-auth-cli-check-head dashboard-auth-cli-border">
            <span>+-- REQUESTING SESSION... </span>
            <span class="dashboard-auth-cli-check-rail">{{ progressRailGlyphs }}</span>
            <span> {{ progressLabel }} :: {{ progressStatus }} --+</span>
          </div>
          <div class="dashboard-auth-cli-check-bar dashboard-auth-cli-progress">
            <span class="dashboard-auth-cli-progress-border">|</span>
            <span class="dashboard-auth-cli-progress-track">
              <span class="dashboard-auth-cli-progress-empty">{{ progressTrackGlyphs }}</span>
              <span class="dashboard-auth-cli-progress-fill">
                <span>{{ progressFillGlyphs }}</span>
              </span>
            </span>
            <span class="dashboard-auth-cli-progress-border">|</span>
          </div>
          <div class="dashboard-auth-cli-check-foot dashboard-auth-cli-border">
            <span>+</span>
            <span class="dashboard-auth-cli-check-rail">{{ progressRailGlyphs }}</span>
            <span>+</span>
          </div>
        </div>
        <pre class="dashboard-auth-cli-line dashboard-auth-cli-required">{{ cliHistoryLines[0] }}</pre>
      </div>

      <form class="dashboard-auth-cli-form" @submit.prevent="submit">
        <label class="dashboard-auth-cli-label" for="dashboard-password">PASSWORD REQUIRED:</label>
        <div class="dashboard-auth-cli-entry" @click="focusPasswordInput">
          <span class="dashboard-auth-cli-prompt" aria-hidden="true">&gt;</span>
          <span class="dashboard-auth-cli-secret" aria-hidden="true">{{ passwordEcho }}</span>
          <span class="dashboard-auth-cli-cursor" aria-hidden="true" />
          <input
            id="dashboard-password"
            ref="passwordInput"
            v-model="dashboardPassword"
            aria-label="Password"
            autocomplete="current-password"
            class="dashboard-auth-cli-input"
            name="dashboard-password"
            type="password"
            @blur="restoreWhiteMode"
            @focus="engageLightsOut"
            @keydown.enter.prevent="submit"
          />
        </div>

        <p v-if="error" class="dashboard-auth-error" data-testid="auth-error" role="alert">{{ error }}</p>
      </form>
    </section>
  </main>
</template>
